import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Building2, UtensilsCrossed, ChevronRight } from 'lucide-react';
import { StatsBanner } from '../components/StatsBanner.js';
import { contentEs } from '../data/contentEs.js';
import { contentEn } from '../data/contentEn.js';

interface HomePageProps {
  isEn?: boolean;
}

export const HomePage: React.FC<HomePageProps> = ({ isEn = false }) => {
  const c = isEn ? contentEn : contentEs;

  useEffect(() => {
    document.title = c.meta.title;
  }, [c.meta.title]);

  const b2bPath = isEn ? '/en/business' : '/empresas';
  const b2cPath = isEn ? '/en/locations' : '/sucursales';

  return (
    <div className="flex flex-col">
      <section className="relative min-h-[82vh] lg:min-h-[88vh] flex items-center justify-center overflow-hidden bg-gray-950">
        <div className="absolute inset-0 z-0">
          <img
            src="/website/hero-dona-blanca.png"
            alt="Doña Blanca Restaurante y Operación"
            className="w-full h-full object-cover object-center filter brightness-[1.02] contrast-[1.02] scale-[1.01]"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/30 to-black/20" />
        </div>

        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
          <h1 className="font-heading font-black text-4xl sm:text-6xl lg:text-7xl tracking-tight text-white leading-tight drop-shadow-[0_4px_16px_rgba(0,0,0,0.7)]">
            <span>{c.home.hero.headline} </span>
            <span className="text-[#38BDF8] block sm:inline">{c.home.hero.headlineAccent}</span>
          </h1>

          <p className="mt-6 sm:mt-8 max-w-3xl mx-auto text-lg sm:text-xl text-white font-medium leading-relaxed drop-shadow-[0_2px_10px_rgba(0,0,0,0.85)]">
            {c.home.hero.body}
          </p>

          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to={b2bPath}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2.5 bg-[#001557] hover:bg-[#11255E] text-white font-bold text-base px-8 py-4 rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 border border-blue-400/30"
            >
              <span>{c.home.hero.ctaBusiness}</span>
              <ArrowRight className="w-5 h-5 text-sky-400" />
            </Link>

            <Link
              to={b2cPath}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2.5 bg-[#EAB308] hover:bg-[#D99B00] text-[#001557] font-bold text-base px-8 py-4 rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 border border-[#CA8A04]/50 hover:scale-[1.02]"
            >
              <span>{c.home.hero.ctaLocations}</span>
              <ChevronRight className="w-5 h-5 text-[#001557]" />
            </Link>
          </div>
        </div>
      </section>

      <StatsBanner isEn={isEn} />

      <section className="py-20 lg:py-28 bg-[#FBF9F5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-stretch">
            
            <div className="flex flex-col justify-between bg-white rounded-2xl border border-[#E5E0D8] overflow-hidden shadow-sm hover:shadow-md transition-shadow">
              <div className="relative h-72 sm:h-80 overflow-hidden bg-slate-900">
                <img
                  src="/website/comcorp.png"
                  alt="Soluciones de alimentación para empresas"
                  className="w-full h-full object-cover object-center hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
                <div className="absolute top-4 left-4 inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#001557]/90 text-white text-xs font-bold uppercase tracking-wider backdrop-blur-sm">
                  <Building2 className="w-3.5 h-3.5 text-sky-300" />
                  <span>{c.home.paths.b2b.eyebrow}</span>
                </div>
              </div>

              <div className="p-8 sm:p-10 flex-1 flex flex-col justify-between">
                <div>
                  <h2 className="font-heading font-black text-2xl sm:text-3xl text-[#001557] tracking-tight">
                    {c.home.paths.b2b.title}
                  </h2>
                  <p className="mt-4 text-base text-gray-600 leading-relaxed">
                    {c.home.paths.b2b.text}
                  </p>
                </div>

                <div className="mt-8 pt-6 border-t border-gray-100">
                  <Link
                    to={b2bPath}
                    className="inline-flex items-center gap-2 text-base font-bold text-[#001557] hover:text-[#162B75] group"
                  >
                    <span>{c.home.paths.b2b.cta}</span>
                  </Link>
                </div>
              </div>
            </div>

            <div className="flex flex-col justify-between bg-white rounded-2xl border border-[#E5E0D8] overflow-hidden shadow-sm hover:shadow-md transition-shadow">
              <div className="relative h-72 sm:h-80 overflow-hidden bg-amber-950">
                <img
                  src="/website/sucursales-hero-familia.png"
                  alt="Nuestras sucursales Doña Blanca"
                  className="w-full h-full object-cover object-center hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
                <div className="absolute top-4 left-4 inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#97472E]/90 text-white text-xs font-bold uppercase tracking-wider backdrop-blur-sm">
                  <UtensilsCrossed className="w-3.5 h-3.5 text-amber-200" />
                  <span>{c.home.paths.b2c.eyebrow}</span>
                </div>
              </div>

              <div className="p-8 sm:p-10 flex-1 flex flex-col justify-between">
                <div>
                  <h2 className="font-heading font-black text-2xl sm:text-3xl text-[#001557] tracking-tight">
                    {c.home.paths.b2c.title}
                  </h2>
                  <p className="mt-4 text-base text-gray-600 leading-relaxed">
                    {c.home.paths.b2c.text}
                  </p>
                </div>

                <div className="mt-8 pt-6 border-t border-gray-100">
                  <Link
                    to={b2cPath}
                    className="inline-flex items-center gap-2 text-base font-bold text-[#001557] hover:text-[#162B75] group"
                  >
                    <span>{c.home.paths.b2c.cta}</span>
                  </Link>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>
    </div>
  );
};
